import React, { useState } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { smsSupported, smsPermission, requestSmsPermission, scanInbox, rememberImported, rememberSkipped, forgetSkipped, autoSmsEnabled, setAutoSms, daysToScan, rememberScan, lastScanAt } from "../../sms/smsInbox";
import { findRecurring, readDismissed, rememberDismissed } from "../../sms/recurring";
import { scheduleLabel, nextOccurrence } from "../../utils/billFrequency";
import { sourceForDigits } from "../../utils/moneySources";
import { EXPENSE_TYPES } from "../../utils/expenseTypes";
import { spendingType } from "../../sms/parseSms";
import { findDiscoveries, dismissDiscovery } from "../../sms/discover";
import { Card, SectionTitle, Sheet, Button, Chip, Field, useInputStyle, useMoney, formatDate, todayIso } from "../ui";

const KIND = {
  expense: { label: "Paid", tone: "negative" },
  income: { label: "Received", tone: "positive" },
  bill: { label: "Due", tone: "pending" },
};

// The account or card a message went through, as fields for the entry: matched by the
// last 4 digits it mentions, to an account / card that was already added by its date.
// Money received only goes into a bank account.
const sourceFields = (entry, data) => {
  const source = sourceForDigits(entry.digits, entry.date, data.assets, data.cards);
  if (!source) return {};
  if (entry.kind === "income") return source.kind === "account" ? { receivedIntoId: source.id } : {};
  return { paidFromKind: source.kind, paidFromId: source.id };
};

// The category the person already uses for this merchant: the type of their latest
// entry with the same name, when it's an everyday one they picked (or changed to).
// Their choice beats the guess from the message. Null when there's none.
const EVERYDAY_TYPES = new Set(EXPENSE_TYPES.filter((t) => t !== "Lent"));
export const learnedType = (payee, bills) => {
  const name = String(payee || "").trim().toLowerCase();
  if (!name) return null;
  const same = bills.filter((b) => String(b.payee || "").trim().toLowerCase() === name && EVERYDAY_TYPES.has(b.type) && b.type !== "Purchase");
  if (!same.length) return null;
  return same.sort((a, b) => (a.dueDate < b.dueDate ? 1 : -1))[0].type;
};

// Entries that come around again (a bill, an EMI, a subscription) are labelled monthly;
// everyday spending is one-time.
const REPEATING_TYPES = new Set(["Bills", "Investments", "Loans", "Subscription", "Card"]);

// What each kind of message becomes when it is added.
const toRequest = (entry, data) => {
  const common = { amount: entry.amount, description: entry.description, smsKey: entry.key, ...sourceFields(entry, data) };
  if (entry.kind === "income") {
    return { collection: "income", body: { ...common, source: entry.payee, type: entry.type, date: entry.date, status: "Received" } };
  }
  if (!REPEATING_TYPES.has(entry.type)) entry = { ...entry, type: learnedType(entry.payee, data.bills) || entry.type };
  const recurring = REPEATING_TYPES.has(entry.type);
  return {
    collection: "bills",
    body: {
      ...common,
      payee: entry.payee,
      type: entry.type,
      schedule: recurring ? "Monthly" : "One-time",
      dueDate: entry.date,
      status: entry.kind === "bill" ? "Pending" : "Paid",
      autopay: entry.autopay,
      // An investment payment joins one already on Finances; a new investment is only
      // made if the person says yes (see DiscoverySheet).
      linkOnly: true,
    },
  };
};

// Fingerprints of the messages entries in the app were made from (or settled by). A
// message counts as already added only if its entry still exists.
const savedSmsKeys = (data) => new Set([...data.bills, ...data.income].map((r) => r.smsKey).filter(Boolean));

const dayGap = (a, b) => Math.abs(new Date(`${a}T00:00:00`) - new Date(`${b}T00:00:00`)) / 86400000;
const MATCH_DAYS = 7;
const VARIABLE_MATCH_DAYS = 10;

// Words of a name, to tell whether an SMS and a bill are about the same thing.
const words = (s) => new Set(String(s || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2 && !["bill", "payment", "via", "the"].includes(w)));
const sharesWord = (a, b) => {
  const wa = words(a);
  return [...words(b)].some((w) => wa.has(w));
};

// A bill whose amount changes each time can't be matched by amount. It matches a debit
// SMS around its due date that shares a word with its name (or its bill's), or - when
// it's the only such bill of that type due then - one of the same type.
const matchVariable = (e, bills, used) => {
  const open = bills.filter((b) => !used.has(`b${b.id}`) && b.status === "Pending" && b.variableAmount && dayGap(b.dueDate, e.date) <= VARIABLE_MATCH_DAYS);
  const byName = open.find((b) => sharesWord(b.payee, e.payee) || sharesWord(b.payee, e.description));
  if (byName) return byName;
  const sameType = open.filter((b) => b.type === e.type);
  return sameType.length === 1 ? sameType[0] : null;
};

// A debit SMS that matches something already Pending (an EMI, a SIP instalment, a
// bill) settles that entry instead of adding a second one. Same for a credit SMS
// and income that's expected (a salary). Each existing entry is matched at most once.
export const matchExisting = (entries, data) => {
  const used = new Set();
  const matches = new Map();
  for (const e of entries) {
    let match = null;
    if (e.kind === "expense" || e.kind === "bill") {
      match =
        data.bills.find(
          (b) => !used.has(`b${b.id}`) && b.status === "Pending" && Math.abs(b.amount - e.amount) <= Math.max(1, b.amount * 0.01) && dayGap(b.dueDate, e.date) <= MATCH_DAYS
        ) || matchVariable(e, data.bills, used);
      // A "due soon" notice for something already on the list adds nothing new.
      if (match) matches.set(e.key, { collection: e.kind === "bill" ? "none" : "bills", row: match, label: match.payee });
    } else if (e.kind === "income") {
      match = data.income.find(
        (i) =>
          !used.has(`i${i.id}`) &&
          i.status !== "Received" &&
          dayGap(i.date, e.date) <= MATCH_DAYS &&
          (Math.abs(i.amount - e.amount) <= i.amount * 0.1 || (i.type === "Salary" && e.type === "Salary"))
      );
      if (match) matches.set(e.key, { collection: "income", row: match, label: match.source });
    }
    if (match) used.add(`${e.kind === "income" ? "i" : "b"}${match.id}`);
  }
  return matches;
};

// Adds messages to the app: each one settles the matching Pending entry (see
// matchExisting) or becomes a new one. Returns the keys handled and how many failed;
// handled ones are remembered so they aren't read again.
export const importEntries = async (entries, data, matches = matchExisting(entries, data)) => {
  const done = [];
  let failed = 0;
  let settledIncome = false;
  for (const entry of entries) {
    const match = matches.get(entry.key);
    if (match && match.collection === "none") {
      done.push(entry.key);
      continue;
    }
    if (match) {
      // Settle the existing entry. data.update never throws; on a problem it re-syncs.
      // It keeps an account / card it already had; otherwise the message's is used.
      const hasSource = match.collection === "bills" ? !!match.row.paidFromKind : !!match.row.receivedIntoId;
      const source = hasSource ? {} : sourceFields(entry, data);
      // A bill whose amount changes each time takes the amount the SMS says was paid.
      const changes =
        match.collection === "bills"
          ? { status: "Paid", smsKey: entry.key, ...(match.row.variableAmount ? { amount: entry.amount } : {}), ...source }
          : { status: "Received", amount: entry.amount, date: entry.date, smsKey: entry.key, ...source };
      await data.update(match.collection, match.row.id, changes);
      if (match.collection === "income") settledIncome = true;
      done.push(entry.key);
      continue;
    }
    const { collection, body } = toRequest(entry, data);
    try {
      await data.create(collection, body);
      done.push(entry.key);
    } catch (err) {
      // "Already added" means an earlier import (maybe from another phone) has it.
      if (/already added/i.test(err.message)) done.push(entry.key);
      else failed += 1;
    }
  }
  rememberImported(done);
  // A received salary lines up the next one on the server.
  if (settledIncome) data.reload();
  return { done, failed };
};

// Reading new messages by itself, when the app opens or comes back to the front: since
// this phone last read them (or, the first time, since the newest entry in the app).
// Only with SMS permission already given - it never asks by itself - and the setting on.
// Returns { added, failed }, or null when it didn't run.
let autoRunning = false;
export const autoImportSms = async (data) => {
  if (autoRunning || !smsSupported() || !autoSmsEnabled()) return null;
  autoRunning = true;
  try {
    if ((await smsPermission()) !== "granted") return null;
    const dates = [...data.bills.map((b) => b.dueDate), ...data.income.map((i) => i.date)].filter((d) => d && d <= todayIso());
    const latest = dates.length ? dates.sort()[dates.length - 1] : null;
    const { fresh, all } = await scanInbox(daysToScan(latest), savedSmsKeys(data));
    rememberScan();
    // A card, salary or investment the app doesn't have yet: asked about, not added.
    const discoveries = findDiscoveries(all, data);
    if (!fresh.length) return { added: 0, failed: 0, discoveries };
    const { done, failed } = await importEntries(fresh, data);
    return { added: done.length, failed, discoveries };
  } catch {
    return null;
  } finally {
    autoRunning = false;
  }
};

// ---- things found in SMS that the app doesn't have yet: ask before adding ----------------

const DiscoveryCard = ({ item, data, onSettled }) => {
  const { tokens } = useTheme();
  const input = useInputStyle();
  const money = useMoney();
  const next = item.date ? nextOccurrence(item.date, "months", 1) : todayIso();
  const [form, setForm] = useState(() => ({
    name: item.kind === "card" ? item.name : item.kind === "salary" ? item.source : item.name,
    amount: item.amount ? String(item.amount) : "",
    limit: "",
    billingDay: "1",
    nextDate: next,
  }));
  const [state, setState] = useState("open"); // open | saving | added | dismissed
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Entries already imported from these messages, to link to what's added now.
  const importedFrom = (keys) => data.bills.filter((b) => b.smsKey && keys.includes(b.smsKey));

  const add = async () => {
    const amount = parseFloat(form.amount);
    if (!form.name.trim()) return setError("Give it a name.");
    setState("saving");
    setError("");
    try {
      if (item.kind === "card") {
        const limit = form.limit.trim() === "" ? 0 : parseFloat(form.limit);
        if (Number.isNaN(limit) || limit < 0) throw new Error("Enter the card's limit, or leave it empty.");
        const card = await data.create("cards", { name: form.name.trim(), cardLimit: limit, balance: 0, billingDay: parseInt(form.billingDay, 10), last4: item.last4 });
        // Its earlier spends say they were paid with it (they don't change what's owed:
        // that starts from today).
        for (const b of importedFrom(item.keys)) if (!b.paidFromKind) await data.update("bills", b.id, { paidFromKind: "card", paidFromId: card.id });
      } else if (item.kind === "salary") {
        if (Number.isNaN(amount) || amount <= 0) throw new Error("Enter the salary amount.");
        const account = item.digits ? data.assets.find((a) => a.category === "account" && a.last4 === item.digits) : null;
        await data.create("income", {
          source: form.name.trim(),
          type: "Salary",
          date: form.nextDate,
          amount,
          status: "Upcoming",
          repeatUnit: "months",
          repeatEvery: 1,
          receivedIntoId: account ? account.id : null,
        });
      } else {
        if (Number.isNaN(amount) || amount <= 0) throw new Error("Enter the instalment amount.");
        const schedule = { repeatUnit: "months", repeatEvery: 1, amount, startDate: form.nextDate };
        if (item.existing) await data.update("assets", item.existing.id, schedule);
        // Starts with what these messages already paid into it.
        else await data.create("assets", { name: form.name.trim(), type: "SIP / Mutual fund", category: "investment", value: item.total, ...schedule });
        // The payments already imported now count as its instalments.
        for (const b of importedFrom(item.keys)) if (!b.sourceKind) await data.update("bills", b.id, { type: "Investments", payee: form.name.trim() });
        data.reload();
      }
      setState("added");
      onSettled();
    } catch (err) {
      setError(err.message);
      setState("open");
    }
  };

  const dismiss = () => {
    dismissDiscovery(item.id);
    setState("dismissed");
    onSettled();
  };

  // Only this item's own kind is worked out: a card has no amount, a salary no list of
  // payments, so building the others' text would fail.
  const title =
    item.kind === "card" ? `New card ••${item.last4}` : item.kind === "salary" ? `Salary from ${item.source}` : item.existing ? `Make ${item.name} a monthly investment` : `New investment: ${item.name}`;
  const hint = () => {
    if (item.kind === "card") return `Spends on a card ending ${item.last4} are in your SMS, but it isn't in your cards. Add it to track what you owe on it; its spends are then paid from it.`;
    if (item.kind === "salary") return `${money(item.amount || 0)} came in as salary. Add it as a monthly salary: it's expected each month, you're reminded to mark it received, and the next one is lined up.`;
    const count = (item.keys || []).length;
    return `${count} payment${count === 1 ? "" : "s"} to it in your SMS (${money(item.total || 0)}). ${item.existing ? "Set it to repeat" : "Add it to your investments"} so each instalment is expected and counted in what you've invested.`;
  };

  if (state === "added" || state === "dismissed") {
    return (
      <Card style={{ marginTop: 12, padding: 14 }}>
        <div style={{ fontSize: 13, color: state === "added" ? tokens.positive : tokens.textMuted }}>
          {title}: {state === "added" ? "added." : "not added. You won't be asked again."}
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>{title}</div>
      <div style={{ margin: "4px 0 12px", fontSize: 12, color: tokens.textMuted }}>{hint()}</div>
      <Field label={item.kind === "card" ? "Card name" : item.kind === "salary" ? "From" : "Investment name"}>
        <input style={input} value={form.name} onChange={set("name")} />
      </Field>
      {item.kind === "card" ? (
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Limit (₹, optional)"><input style={input} type="number" inputMode="decimal" value={form.limit} onChange={set("limit")} /></Field></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Field label="Bill day">
              <select style={input} value={form.billingDay} onChange={set("billingDay")}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}><Field label={item.kind === "salary" ? "Amount (₹)" : "Each month (₹)"}><input style={input} type="number" inputMode="decimal" value={form.amount} onChange={set("amount")} /></Field></div>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Next one on"><input style={input} type="date" value={form.nextDate} onChange={set("nextDate")} /></Field></div>
        </div>
      )}
      {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
      <div style={{ display: "flex", gap: 10 }}>
        <Button style={{ flex: 1 }} disabled={state === "saving"} onClick={add}>{state === "saving" ? "Adding…" : "Add"}</Button>
        <Button kind="ghost" style={{ flex: 1 }} disabled={state === "saving"} onClick={dismiss}>Not now</Button>
      </div>
    </Card>
  );
};

// A card, a salary or an investment spotted in SMS that isn't in the app yet: each is
// only added if the person says so.
export const DiscoverySheet = ({ items, data, onClose }) => {
  const { tokens } = useTheme();
  const [left, setLeft] = useState(items.length);
  return (
    <Sheet title={`Found in your SMS`} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>
        Your messages mention {items.length === 1 ? "something" : `${items.length} things`} that isn't in budgetsprint yet. Add {items.length === 1 ? "it" : "the ones you want"}; nothing is added without asking.
      </p>
      {items.map((item) => (
        <DiscoveryCard key={item.id} item={item} data={data} onSettled={() => setLeft((n) => n - 1)} />
      ))}
      <Button kind={left === 0 ? "primary" : "ghost"} style={{ width: "100%", marginTop: 16 }} onClick={onClose}>
        {left === 0 ? "Done" : "Decide later"}
      </Button>
    </Sheet>
  );
};

const ReviewSheet = ({ entries, data, onClose }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const [matches] = useState(() => matchExisting(entries, data));
  const [selected, setSelected] = useState(() => new Set(entries.map((e) => e.key)));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const add = async () => {
    setBusy(true);
    const { done, failed } = await importEntries(entries.filter((e) => selected.has(e.key)), data, matches);
    rememberSkipped(entries.filter((e) => !selected.has(e.key)).map((e) => e.key));
    setBusy(false);
    if (failed === 0) return onClose();
    setResult(`${done.length} added. ${failed} could not be saved. Check your connection and scan again.`);
  };

  const skipAll = () => {
    rememberSkipped(entries.map((e) => e.key));
    onClose();
  };

  return (
    <Sheet title={`Found ${entries.length} ${entries.length === 1 ? "message" : "messages"}`} onClose={onClose}>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: tokens.textMuted }}>
        These came from bank, UPI, FASTag, insurance and bill messages. Untick anything you don't want. Nothing is added until you tap the button.
      </p>

      {entries.map((e) => {
        const kind = KIND[e.kind];
        const on = selected.has(e.key);
        return (
          <button
            key={e.key}
            onClick={() => toggle(e.key)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 0", background: "none", border: "none", borderTop: `1px solid ${tokens.surfaceBorder}`, textAlign: "left", cursor: "pointer", color: tokens.textPrimary, opacity: on ? 1 : 0.45 }}
          >
            <input type="checkbox" checked={on} readOnly style={{ width: 20, height: 20, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.payee}</div>
              <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>
                {kind.label} {formatDate(e.date)} · {e.type}
                {e.autopay ? " · Autopay" : ""}
              </div>
              {matches.has(e.key) && (
                <div style={{ fontSize: 12, fontWeight: 700, color: tokens.positive, marginTop: 2 }}>
                  {e.kind === "bill"
                    ? `Already on your list as "${matches.get(e.key).label}"`
                    : `Marks "${matches.get(e.key).label}" as ${e.kind === "income" ? "received" : "paid"}`}
                </div>
              )}
            </div>
            <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: e.kind === "income" ? tokens.positive : tokens.textPrimary }}>
              {e.kind === "income" ? "+" : ""}
              {money(e.amount)}
            </div>
          </button>
        );
      })}

      {result && <div role="alert" style={{ margin: "12px 0", fontSize: 13, color: tokens.negative }}>{result}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
        <Button disabled={busy || selected.size === 0} onClick={add}>
          {busy ? "Adding…" : `Add ${selected.size} selected`}
        </Button>
        <Button kind="ghost" disabled={busy} onClick={skipAll}>Skip all of these</Button>
      </div>
    </Sheet>
  );
};

// ---- repeating payments ------------------------------------------------------------

// What the person can say a repeating payment is, and the bill type it's saved as.
const RECURRING_KINDS = [
  { label: "Bill", type: "Bills" },
  { label: "Subscription", type: "Subscription" },
  { label: "Loan EMI", type: "Loans" },
  { label: "Investment", type: "Investments" },
  { label: "Card bill", type: "Card" },
  { label: "Family", type: "Family" },
  { label: "Other", type: "Purchase" },
];

// A sensible first pick: what the SMS already said, and a plain purchase that
// repeats on schedule is most likely a subscription.
const guessType = (s) => (s.type && s.type !== "Purchase" ? s.type : s.repeatUnit === "days" ? "Bills" : "Subscription");

const SuggestionCard = ({ suggestion, data, onSettled }) => {
  const { tokens, fonts } = useTheme();
  const input = useInputStyle();
  const money = useMoney();
  const [type, setType] = useState(() => guessType(suggestion));
  const [amount, setAmount] = useState(String(suggestion.amount));
  const [dueDate, setDueDate] = useState(suggestion.nextDate);
  const [state, setState] = useState("open"); // open | saving | added | dismissed
  const [error, setError] = useState("");

  const accept = async () => {
    const value = parseFloat(amount);
    if (Number.isNaN(value) || value <= 0) return setError("Enter a valid amount.");
    if (!dueDate) return setError("Choose when the next one is due.");
    setState("saving");
    setError("");
    try {
      await data.create("bills", {
        payee: suggestion.payee,
        type,
        schedule: scheduleLabel(suggestion.repeatUnit, suggestion.repeatEvery),
        dueDate,
        amount: value,
        status: "Pending",
        autopay: false,
        description: `Found in SMS: paid ${suggestion.count} times, ${suggestion.label}`,
        repeatUnit: suggestion.repeatUnit,
        repeatEvery: suggestion.repeatEvery,
      });
      rememberDismissed([suggestion.key]); // don't ask again, it's tracked now
      setState("added");
      onSettled();
    } catch (err) {
      setError(err.message);
      setState("open");
    }
  };

  const dismiss = () => {
    rememberDismissed([suggestion.key]);
    setState("dismissed");
    onSettled();
  };

  if (state === "added" || state === "dismissed") {
    return (
      <Card style={{ marginTop: 10, padding: "12px 16px", fontSize: 13, color: state === "added" ? tokens.positive : tokens.textMuted }}>
        {state === "added" ? `Added "${suggestion.payee}" to Bills & Payments, due ${formatDate(dueDate)}.` : `OK, "${suggestion.payee}" won't be suggested again.`}
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{suggestion.payee}</div>
          <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>
            Paid {suggestion.count} times, {suggestion.label} · last on {formatDate(suggestion.lastDate)}
          </div>
        </div>
        <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary, whiteSpace: "nowrap" }}>{money(suggestion.amount)}</div>
      </div>

      <div style={{ margin: "12px 0 6px", fontSize: 12, fontWeight: 600, color: tokens.textMuted }}>What is it?</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {RECURRING_KINDS.map((k) => (
          <Chip key={k.type} active={type === k.type} onClick={() => setType(k.type)}>{k.label}</Chip>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}><Field label="Amount (₹)"><input style={input} type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field></div>
        <div style={{ flex: 1, minWidth: 0 }}><Field label="Next due"><input style={input} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field></div>
      </div>

      {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
      <div style={{ display: "flex", gap: 10 }}>
        <Button style={{ flex: 1 }} disabled={state === "saving"} onClick={accept}>{state === "saving" ? "Adding…" : "Add to Bills"}</Button>
        <Button kind="ghost" style={{ flex: 1 }} disabled={state === "saving"} onClick={dismiss}>Not repeating</Button>
      </div>
    </Card>
  );
};

// Shown after a scan when the same payment turns up at a regular gap. Nothing is
// added unless the person says what it is and taps Add.
const RecurringSheet = ({ suggestions, data, onClose }) => {
  const { tokens } = useTheme();
  const [left, setLeft] = useState(suggestions.length);
  return (
    <Sheet title={`${suggestions.length} repeating ${suggestions.length === 1 ? "payment" : "payments"} found`} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>
        These were paid to the same place, for about the same amount, at a regular gap. Tell us what each one is to track it in Bills &amp; Payments. You'll get a reminder when it's due, and the next bank SMS for it marks it paid.
      </p>
      {suggestions.map((s) => (
        <SuggestionCard key={s.key} suggestion={s} data={data} onSettled={() => setLeft((n) => n - 1)} />
      ))}
      <Button kind={left === 0 ? "primary" : "ghost"} style={{ width: "100%", marginTop: 16 }} onClick={onClose}>
        {left === 0 ? "Done" : "Decide later"}
      </Button>
    </Sheet>
  );
};

// Payments to look for patterns in: debits in the scanned messages (including ones
// imported before), plus paid entries already saved in the app.
const recurringSuggestions = (all, data) =>
  findRecurring(
    [
      ...all.filter((e) => e.kind === "expense").map((e) => ({ payee: e.payee, amount: e.amount, date: e.date, type: e.type })),
      ...data.bills.filter((b) => b.status === "Paid" && !b.sourceKind).map((b) => ({ payee: b.payee, amount: b.amount, date: b.dueDate, type: b.type })),
    ],
    { tracked: data.bills, dismissed: readDismissed() }
  );

const SmsImportCard = ({ data }) => {
  const { tokens } = useTheme();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [entries, setEntries] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [discoveries, setDiscoveries] = useState(null);
  const [skippedFound, setSkippedFound] = useState(null); // messages skipped before, found by the last scan
  const [showDiscoveries, setShowDiscoveries] = useState(false);
  const [auto, setAuto] = useState(autoSmsEnabled);
  const [lastRead, setLastRead] = useState(lastScanAt);

  if (!smsSupported()) return null;

  const toggleAuto = (on) => {
    setAuto(on);
    setAutoSms(on);
  };

  // SMS entries saved under "Purchase" before categories were worked out, that can now
  // be put where they belong: the person's own category for that merchant, or the one
  // its name or message points to.
  const resortable = data.bills
    .filter((b) => b.type === "Purchase" && String(b.description || "").startsWith("From SMS"))
    .map((b) => ({ bill: b, type: learnedType(b.payee, data.bills) || spendingType(`${b.payee} ${b.description}`) }))
    .filter((r) => r.type && r.type !== "Purchase");
  const resort = async () => {
    setBusy(true);
    for (const { bill, type } of resortable) await data.update("bills", bill.id, { type });
    setBusy(false);
    setNote(`${resortable.length} ${resortable.length === 1 ? "entry was" : "entries were"} moved out of Purchase.`);
  };

  const scan = async (days) => {
    setBusy(true);
    setNote("");
    try {
      let permission = await smsPermission();
      if (permission !== "granted") permission = await requestSmsPermission();
      if (permission !== "granted") {
        setNote("Permission was not given. You can allow SMS for budgetsprint in Android Settings → Apps → budgetsprint → Permissions.");
      } else {
        const { fresh, all, read, imported, skipped, skippedEntries } = await scanInbox(days, savedSmsKeys(data));
        rememberScan();
        setLastRead(Date.now());
        setSkippedFound(skippedEntries.length ? skippedEntries : null);
        const found = recurringSuggestions(all, data);
        setSuggestions(found.length ? found : null);
        const finds = findDiscoveries(all, data);
        setDiscoveries(finds.length ? finds : null);
        // New messages are reviewed first; then new cards / salary / investments, then
        // repeating payments, so an older message can't settle a bill only just added.
        if (fresh.length) setEntries(fresh);
        else if (finds.length) setShowDiscoveries(true);
        else if (found.length) setShowSuggestions(true);
        else {
          // Say what was read, so "nothing new" doesn't look like nothing was read.
          const useful = all.length;
          const parts = [];
          if (imported) parts.push(`${imported} already added`);
          if (skipped) parts.push(`${skipped} you skipped before`);
          setNote(
            useful === 0
              ? `Read ${read} messages from the last ${days} days; none of them were bank, UPI or bill messages.`
              : `Read ${read} messages from the last ${days} days: ${useful} bank or bill ${useful === 1 ? "message" : "messages"}, ${parts.join(" and ")}. Nothing new to add.`
          );
        }
      }
    } catch (err) {
      setNote(err && err.message ? err.message : "Could not read messages.");
    }
    setBusy(false);
  };

  const closeReview = () => {
    setEntries(null);
    if (discoveries) setShowDiscoveries(true);
    else if (suggestions) setShowSuggestions(true);
  };
  const closeDiscoveries = () => {
    setShowDiscoveries(false);
    setDiscoveries(null);
    if (suggestions) setShowSuggestions(true);
  };

  return (
    <>
      <SectionTitle>Bank &amp; bill messages</SectionTitle>
      <Card>
        <div style={{ fontSize: 14, color: tokens.textPrimary }}>
          Add expenses, income and upcoming bills from your SMS: bank and UPI alerts, FASTag, LIC, electricity, mobile, internet and more.
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: tokens.textMuted }}>
          Messages are read on this phone only and never uploaded; only the entries made from them are saved. Messages from people's phone numbers are ignored.
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 12, fontSize: 14, color: tokens.textPrimary, cursor: "pointer" }}>
          <input type="checkbox" checked={auto} onChange={(e) => toggleAuto(e.target.checked)} style={{ width: 20, height: 20, marginTop: 1 }} />
          <span>
            Add new messages by themselves
            <span style={{ display: "block", fontSize: 12, color: tokens.textMuted }}>
              Each time the app opens, messages since it last looked are added (or settle a matching bill). Needs SMS allowed: tap a scan below once.
              {lastRead ? ` Last looked ${new Date(lastRead).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.` : ""}
            </span>
          </span>
        </label>
        <div style={{ marginTop: 8, fontSize: 12, color: tokens.textMuted }}>
          Payments that repeat, like subscriptions, EMIs and bills, are spotted too, and you're asked whether to track them. Scan 90 days to catch monthly ones.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
          <Button disabled={busy} onClick={() => scan(30)}>{busy ? "Reading…" : "Scan the last 30 days"}</Button>
          <Button kind="ghost" disabled={busy} onClick={() => scan(90)}>Scan the last 90 days</Button>
          {resortable.length > 0 && (
            <Button kind="ghost" disabled={busy} onClick={resort}>
              Sort {resortable.length} SMS {resortable.length === 1 ? "entry" : "entries"} out of Purchase
            </Button>
          )}
        </div>
        {note && <div style={{ marginTop: 10, fontSize: 12, color: tokens.textMuted }}>{note}</div>}
        {skippedFound && !entries && (
          <Button
            kind="ghost"
            disabled={busy}
            style={{ width: "100%", marginTop: 10 }}
            onClick={() => {
              forgetSkipped(skippedFound.map((e) => e.key));
              setEntries(skippedFound);
              setSkippedFound(null);
              setNote("");
            }}
          >
            Review the {skippedFound.length} skipped {skippedFound.length === 1 ? "message" : "messages"} again
          </Button>
        )}
      </Card>

      {entries && <ReviewSheet entries={entries} data={data} onClose={closeReview} />}
      {showDiscoveries && discoveries && <DiscoverySheet items={discoveries} data={data} onClose={closeDiscoveries} />}
      {showSuggestions && suggestions && (
        <RecurringSheet suggestions={suggestions} data={data} onClose={() => { setShowSuggestions(false); setSuggestions(null); }} />
      )}
    </>
  );
};

export default SmsImportCard;
